import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Share2, MapPin, Star, MessageCircle, ShoppingBag, Truck, Package, ChevronLeft, ChevronRight, Rocket, Trophy, Eye, KeyRound } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/common/SEO';
import { trackProductView as trackAnalyticsView, trackInitiateCheckout } from '../services/analytics';
import { trackProductView as trackRecentView } from '../components/common/RecentlyViewed';
import { useApp } from '../context/AppContext';
import { useCurrency } from '../context/CurrencyContext';
import { listingSymbol } from '../utils/regionUtils';
import DeliveredPriceCalculator from '../components/product/DeliveredPriceCalculator';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ShareModal from '../components/common/ShareModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { products, getUserById, isAuthenticated, user, startConversation, makeOffer, toggleLike } = useApp();
  const { formatPriceWithOriginal, currency } = useCurrency();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [seller, setSeller] = useState(null);
  const [product, setProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [pcAccess, setPcAccess] = useState(null); // null = unknown, true/false once checked

  // 18+ pieces are only shown to Private Collection members (seller can always view their own)
  useEffect(() => {
    if (!product?.is_adult) { setPcAccess(true); return; }
    if (user?.id && (product.seller_id === user.id || product.sellerId === user.id)) { setPcAccess(true); return; }
    if (!user?.id) { setPcAccess(false); return; }
    fetch(`${BACKEND_URL}/api/private-collection/status?user_id=${user.id}`)
      .then(r => r.json())
      .then(d => setPcAccess(!!d.member))
      .catch(() => setPcAccess(false));
  }, [product, user?.id]);

  // Fetch fresh product from API (not cached context) to get accurate view count
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/products/${id}`);
        if (res.ok) {
          const data = await res.json();
          setProduct(data);
        } else {
          // Fallback to context
          const cached = products.find(p => p.id === id || p.id === parseInt(id) || String(p.id) === id);
          setProduct(cached || null);
        }
      } catch {
        const cached = products.find(p => p.id === id || String(p.id) === id);
        setProduct(cached || null);
      } finally {
        setLoadingProduct(false);
      }
    };
    fetchProduct();
  }, [id, products]);

  // Load seller info
  useEffect(() => {
    const loadSeller = async () => {
      if (product) {
        const sellerData = await getUserById(product.sellerId || product.seller_id);
        setSeller(sellerData);
      }
    };
    loadSeller();
  }, [product, getUserById]);

  useEffect(() => {
    if (product) {
      setOfferPrice(Math.floor(product.price * 0.9).toString());
      // Set liked state from product data
      if (user?.id) {
        setIsLiked((product.liked_by || []).includes(user.id));
      }
      // Track product view for analytics
      trackAnalyticsView(product);
      // Track for recently viewed
      trackRecentView(product, user?.id);
      
      // Track share referral click (earn 50c for sharer)
      const refId = searchParams.get('ref');
      if (refId && refId !== user?.id) {
        // Someone clicked a shared link - award credit to sharer
        const visitorId = localStorage.getItem('visitorId') || `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('visitorId', visitorId);
        
        fetch(`${BACKEND_URL}/api/share/track-click`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sharer_id: refId,
            product_id: product.id,
            visitor_id: visitorId
          })
        }).catch(console.error);
      }
    }
  }, [product, user?.id, searchParams]);

  if (loadingProduct) {
    return (
      <Layout title="Loading..." showBack>
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-minted-300 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout title="Product Not Found" showBack>
        <div className="flex flex-col items-center justify-center py-20">
          <Package className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Product Not Found</h2>
          <p className="text-gray-500 mb-4">This listing may have been removed</p>
          <Button onClick={() => navigate('/')} className="bg-minted-600 hover:bg-minted-700">
            Browse Products
          </Button>
        </div>
      </Layout>
    );
  }

  if (product.is_adult && pcAccess !== true) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ backgroundColor: '#120d0b' }} data-testid="pc-product-gate">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5" style={{ border: '1px solid rgba(201,169,98,0.5)' }}>
          <KeyRound className="w-6 h-6" style={{ color: '#c9a962' }} strokeWidth={1.3} />
        </div>
        <p className="text-[10px] tracking-[0.35em] uppercase" style={{ color: 'rgba(201,169,98,0.7)' }}>Members Only · 18+</p>
        <h2 className="text-xl mt-2" style={{ fontFamily: 'Georgia, serif', color: '#f0e9df' }}>The Private Collection</h2>
        <p className="text-sm mt-3 max-w-xs" style={{ color: '#a89a8c' }}>
          {pcAccess === null ? 'Checking your membership…' : 'This piece is reserved for members of our private 18+ boutique.'}
        </p>
        {pcAccess === false && (
          <button onClick={() => navigate('/private-collection')} data-testid="pc-gate-enter-btn"
            className="mt-7 px-8 py-3 rounded-sm text-xs tracking-[0.2em] uppercase font-semibold"
            style={{ backgroundColor: '#c9a962', color: '#120d0b' }}>
            Request entry
          </button>
        )}
        <button onClick={() => navigate('/')} className="mt-4 text-xs tracking-wider uppercase" style={{ color: '#7a6c5f' }}>
          Back to Minted
        </button>
      </div>
    );
  }

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to make a purchase');
      navigate('/login');
      return;
    }
    // Track checkout initiation
    trackInitiateCheckout(product);
    navigate(`/checkout/${product.id}`);
  };

  const handleLike = () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to like items');
      return;
    }
    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    toggleLike(product.id);
    toast.success(wasLiked ? 'Removed from favorites' : 'Added to favorites!');
  };

  const handleMakeOffer = () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to make an offer');
      navigate('/login');
      return;
    }
    const offer = makeOffer(product.id, parseFloat(offerPrice), offerMessage);
    toast.success('Offer sent successfully!');
    setShowOfferModal(false);
  };

  // Check if current user is the seller
  const isOwnListing = user && (user.id === product.seller_id || user.id === product.sellerId);

  const handleSendMessage = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to message seller');
      navigate('/login');
      return;
    }
    
    // Use seller_id (from API) or sellerId (camelCase fallback)
    const sellerId = product.seller_id || product.sellerId;
    
    if (!sellerId) {
      toast.error('Unable to message seller');
      return;
    }
    
    try {
      await startConversation(sellerId, product.id, message);
      toast.success('Message sent!');
      setShowMessageModal(false);
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  const hasImages = product.images && product.images.length > 0 && product.images[0];

  return (
    <>
      {/* SEO Meta Tags */}
      <SEO
        title={`${product.title} - ${listingSymbol(product.listing_currency)}${product.price}`}
        description={`${product.description?.slice(0, 150)}... Buy now on Minted.`}
        image={product.images?.[0]}
        url={`/product/${product.id}`}
        type="product"
        product={product}
      />

      <div className="min-h-screen bg-[#f7f5f2]" data-testid="product-detail-page">
        {/* Immersive Hero Gallery */}
        <div className="relative" style={{ height: '65vh', minHeight: '380px', maxHeight: '520px' }}>
          {/* Founding Seller Banner */}
          {product.is_founding_seller && (
            <div className="absolute top-0 left-0 right-0 z-30 bg-[#1a3a2a]/90 backdrop-blur-md text-white text-[10px] font-semibold py-2 px-4 flex items-center justify-center gap-1.5 tracking-[0.15em] uppercase">
              <Trophy className="w-3 h-3 text-[#d4af37]" />
              Founding Seller
            </div>
          )}

          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="absolute top-3 left-3 z-30 w-10 h-10 rounded-full bg-white/80 backdrop-blur-md flex items-center justify-center shadow-sm hover:scale-105 transition-transform"
            data-testid="back-button"
            style={{ top: product.is_founding_seller ? '2.5rem' : '0.75rem' }}
          >
            <ChevronLeft className="w-5 h-5 text-[#1a3a2a]" />
          </button>

          {/* Action Buttons (Like + Share) */}
          <div
            className="absolute right-3 z-30 flex gap-2"
            style={{ top: product.is_founding_seller ? '2.5rem' : '0.75rem' }}
          >
            <button
              onClick={handleLike}
              className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md flex items-center justify-center shadow-sm hover:scale-105 transition-transform"
              data-testid="like-button"
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500 text-red-500' : 'text-[#1a3a2a]'}`} />
            </button>
            <button
              onClick={() => setShowShareModal(true)}
              className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md flex items-center justify-center shadow-sm hover:scale-105 transition-transform"
              data-testid="share-button"
            >
              <Share2 className="w-5 h-5 text-[#1a3a2a]" />
            </button>
          </div>

          {/* Image Display */}
          {hasImages ? (
            <div className="w-full h-full">
              <img
                src={product.images[currentImageIndex]}
                alt={product.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  if (e.target.src.includes('minted-marketplace-images')) {
                    try {
                      const key = e.target.src.split('minted-marketplace-images.s3.ap-southeast-2.amazonaws.com/')[1];
                      if (key) e.target.src = `${process.env.REACT_APP_BACKEND_URL}/api/s3-proxy/${key}`;
                    } catch {}
                  }
                }}
              />
              {/* Subtle gradient overlay at bottom for text readability */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          ) : (
            <div className="w-full h-full bg-[#f0ede6] flex items-center justify-center">
              <span className="font-serif text-3xl text-[#1a3a2a]/30 tracking-wider">Minted</span>
            </div>
          )}

          {/* Image Navigation */}
          {hasImages && product.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/70 backdrop-blur-md flex items-center justify-center shadow-sm hover:bg-white/90 transition-colors z-20"
              >
                <ChevronLeft className="w-4 h-4 text-[#1a3a2a]" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/70 backdrop-blur-md flex items-center justify-center shadow-sm hover:bg-white/90 transition-colors z-20"
              >
                <ChevronRight className="w-4 h-4 text-[#1a3a2a]" />
              </button>
              {/* Pagination */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-black/30 backdrop-blur-sm rounded-full px-3 py-1">
                <span className="text-white text-xs font-medium tracking-wider">
                  {currentImageIndex + 1} / {product.images.length}
                </span>
              </div>
            </>
          )}

          {/* Condition badge overlaid */}
          <div className="absolute bottom-4 left-4 z-20 flex gap-2">
            <span className="bg-[#1a3a2a] text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em]">
              {product.condition}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em]">
                {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% Off
              </span>
            )}
          </div>
        </div>

        {/* Product Header Card — overlaps hero slightly */}
        <div className="relative -mt-6 mx-3 bg-white rounded-2xl shadow-[0_8px_32px_rgba(26,58,42,0.06)] border border-[#1a3a2a]/5 px-5 pt-5 pb-4 z-20">
          {product.is_example && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
              <p className="text-amber-800 text-xs"><span className="font-semibold">Example listing</span> — shows how products appear on Minted</p>
            </div>
          )}

          <h1 className="font-serif text-xl tracking-tight text-[#1a3a2a] leading-tight mb-3" style={{ fontFamily: 'Georgia, serif' }}>
            {product.title}
          </h1>

          <div className="flex items-baseline gap-2 mb-1">
            {(() => {
              const p = formatPriceWithOriginal(product.price, product.listing_currency);
              return (
                <>
                  <span className="text-3xl font-serif text-[#1a3a2a] tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>{p.primary}</span>
                  {p.secondary && (
                    <span className="text-sm text-[#a8a29e]">{p.secondary}</span>
                  )}
                </>
              );
            })()}
            {(product.original_price || product.originalPrice) && (product.original_price || product.originalPrice) > product.price && currency === 'AUD' && (!product.listing_currency || product.listing_currency === 'AUD') && (
              <span className="text-sm text-[#a8a29e] line-through">{listingSymbol(product.listing_currency)}{product.original_price || product.originalPrice}</span>
            )}
            {product.previous_price && product.previous_price > product.price && !(product.original_price || product.originalPrice) && (
              <span className="text-sm text-[#d4af37] font-medium">was {listingSymbol(product.listing_currency)}{product.previous_price}</span>
            )}
          </div>
          {/* Price drop alert banner */}
          {product.previous_price && product.previous_price > product.price && (
            <div className="bg-[#d4af37]/10 border border-[#d4af37]/20 rounded-lg px-3 py-2 mt-2 flex items-center gap-2">
              <span className="text-[#856d22] text-xs font-medium">
                Price dropped {Math.round(((product.previous_price - product.price) / product.previous_price) * 100)}% — was {listingSymbol(product.listing_currency)}{product.previous_price}
              </span>
            </div>
          )}
        </div>

        {/* Bento Grid — Details */}
        <div className="mx-3 mt-3 grid grid-cols-2 gap-2.5">
          <div className="bg-white rounded-xl p-3.5 border border-[#1a3a2a]/5">
            <MapPin className="w-4 h-4 text-[#1a3a2a] mb-1.5" />
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#a8a29e] mb-0.5">Location</p>
            <p className="text-sm font-medium text-[#1a3a2a]">{product.location || 'Australia'}</p>
          </div>
          <div className="bg-white rounded-xl p-3.5 border border-[#1a3a2a]/5">
            <Eye className="w-4 h-4 text-[#1a3a2a] mb-1.5" />
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#a8a29e] mb-0.5">Interest</p>
            <p className="text-sm font-medium text-[#1a3a2a]">{product.views || 0} views · {product.likes || 0} likes</p>
          </div>
          {product.shippingAvailable && (
            <div className="bg-[#f0f7f4] rounded-xl p-3.5 border border-[#1a3a2a]/5">
              <Truck className="w-4 h-4 text-[#1a3a2a] mb-1.5" />
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#a8a29e] mb-0.5">Shipping</p>
              <p className="text-sm font-medium text-[#1a3a2a]">Available</p>
              {product.weightClass && (
                <p className="text-[10px] text-[#57534e] mt-0.5">{product.weightClass}</p>
              )}
            </div>
          )}
          {product.pickupAvailable && (
            <div className="bg-[#f0f7f4] rounded-xl p-3.5 border border-[#1a3a2a]/5">
              <Package className="w-4 h-4 text-[#1a3a2a] mb-1.5" />
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#a8a29e] mb-0.5">Pickup</p>
              <p className="text-sm font-medium text-[#1a3a2a]">Available</p>
            </div>
          )}
        </div>

        {/* Delivered price calculator (AU shipping) */}
        {(product.shippingAvailable || product.shipping_available) && (!product.listing_currency || product.listing_currency === 'AUD') && !product.sold && (
          <DeliveredPriceCalculator product={product} />
        )}

        {/* Description */}
        {product.description && (
          <div className="mx-3 mt-3 bg-white rounded-2xl p-5 border border-[#1a3a2a]/5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#a8a29e] mb-2">Description</p>
            <p className="text-[#57534e] text-sm leading-relaxed">{product.description}</p>
          </div>
        )}

        {/* Seller Card */}
        {seller && (
          <div className="mx-3 mt-3 bg-white rounded-2xl p-5 border border-[#1a3a2a]/5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#a8a29e] mb-3">Seller</p>
            <button
              onClick={() => navigate(`/seller/${seller.id}`)}
              className="flex items-center gap-3.5 w-full text-left hover:bg-[#f7f5f2] p-2 -m-2 rounded-xl transition-colors"
              data-testid="seller-card"
            >
              <div className="w-12 h-12 rounded-full overflow-hidden bg-[#1a3a2a] flex-shrink-0 ring-2 ring-[#1a3a2a]/10">
                {seller.avatar ? (
                  <img src={seller.avatar} alt={seller.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-serif text-lg">
                    {seller.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1a3a2a] text-sm">{seller.name}</p>
                <div className="flex items-center gap-1.5 text-xs text-[#57534e] mt-0.5">
                  <Star className="w-3.5 h-3.5 text-[#d4af37] fill-[#d4af37]" />
                  <span>{seller.rating || 0}</span>
                  <span className="text-[#a8a29e]">·</span>
                  <span>{seller.reviewCount || 0} reviews</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#a8a29e] flex-shrink-0" />
            </button>
          </div>
        )}

        {/* Bottom spacer for sticky bar */}
        <div className="h-32" aria-hidden="true" />

        {/* Glassmorphic Sticky Action Bar */}
        <div
          className="fixed left-0 right-0 z-40 px-4 py-3"
          style={{
            bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))',
            background: 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(26,58,42,0.08)',
          }}
          data-testid="product-action-bar"
        >
          <div className="max-w-lg mx-auto flex gap-2.5">
            {isOwnListing ? (
              <>
                <Button
                  variant="outline"
                  className="flex-1 rounded-full border-[#1a3a2a] text-[#1a3a2a] h-11"
                  onClick={() => navigate('/my-listings')}
                  data-testid="my-listings-btn"
                >
                  My Listings
                </Button>
                <Button
                  className="flex-1 rounded-full bg-[#1a3a2a] hover:bg-[#2c5a45] text-white h-11"
                  onClick={() => navigate(`/promote/${product.id}`)}
                  data-testid="promote-btn"
                >
                  <Rocket className="w-4 h-4 mr-1.5" />
                  Boost
                </Button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowMessageModal(true)}
                  className="w-11 h-11 rounded-full border border-[#1a3a2a]/20 flex items-center justify-center hover:bg-[#1a3a2a]/5 transition-colors flex-shrink-0"
                  data-testid="message-seller-btn"
                >
                  <MessageCircle className="w-4.5 h-4.5 text-[#1a3a2a]" />
                </button>
                <Button
                  variant="outline"
                  className="flex-1 rounded-full border-[#1a3a2a] text-[#1a3a2a] h-11 font-medium text-sm"
                  onClick={() => setShowOfferModal(true)}
                  data-testid="make-offer-btn"
                >
                  Make Offer
                </Button>
                <Button
                  className="flex-[1.3] rounded-full bg-[#1a3a2a] hover:bg-[#2c5a45] text-white h-11 font-medium text-sm shadow-[0_4px_16px_rgba(26,58,42,0.2)]"
                  onClick={handleBuyNow}
                  data-testid="buy-now-btn"
                >
                  <ShoppingBag className="w-4 h-4 mr-1.5" />
                  Buy Now
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Make Offer Modal */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif" style={{ fontFamily: 'Georgia, serif' }}>Make an Offer</DialogTitle>
            <DialogDescription>
              Current price: {listingSymbol(product.listing_currency)}{product.price}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Your Offer Price ({listingSymbol(product.listing_currency)})</Label>
              <Input
                type="number"
                value={offerPrice}
                onChange={(e) => setOfferPrice(e.target.value)}
                placeholder="Enter your offer"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Message (optional)</Label>
              <Textarea
                value={offerMessage}
                onChange={(e) => setOfferMessage(e.target.value)}
                placeholder="Add a message to the seller..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferModal(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              className="bg-[#1a3a2a] hover:bg-[#2c5a45] text-white rounded-full"
              onClick={handleMakeOffer}
            >
              Send Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif" style={{ fontFamily: 'Georgia, serif' }}>Message Seller</DialogTitle>
            <DialogDescription>
              Ask about "{product.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi! Is this still available?"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageModal(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              className="bg-[#1a3a2a] hover:bg-[#2c5a45] text-white rounded-full"
              onClick={handleSendMessage}
            >
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
        product={product} 
      />
    </>
  );
};

export default ProductDetailPage;